import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { createContext, useState, useMemo } from 'react';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import Header from './components/Header';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import ContactPage from './pages/ContactPage';

export const ThemeToggleContext = createContext({
  toggleTheme: () => {},
  mode: 'dark' as 'light' | 'dark',
});

export default function App() {
  const [mode, setMode] = useState<'light' | 'dark'>('dark');

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main: '#1EB980',
            light: '#55FFE1',
            dark: '#17a06e',
          },
          secondary: {
            main: '#55FFE1',
          },
          background: {
            default: mode === 'dark' ? '#0A0A0A' : '#F9FAFB',
            paper: mode === 'dark' ? '#151515' : '#FFFFFF',
          },
          text: {
            primary: mode === 'dark' ? '#FFFFFF' : '#1A1A1A',
            secondary: mode === 'dark' ? '#A0A0A0' : '#6B7280',
          },
          // Stage colors for startup journey
          info: {
            main: '#3B82F6', // Pre-Seed blue
          },
          warning: {
            main: '#F59E0B', // Seed orange
          },
          success: {
            main: '#1EB980', // Series A green
          },
          error: {
            main: '#EF4444', // Error red
          },
        },
        typography: {
          fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
          h1: {
            fontWeight: 700,
            fontSize: '3rem',
            lineHeight: 1.2,
            '@media (max-width:600px)': {
              fontSize: '2rem',
            },
          },
          h2: {
            fontWeight: 700,
            fontSize: '2.25rem',
            lineHeight: 1.3,
            '@media (max-width:600px)': {
              fontSize: '1.75rem',
            },
          },
          h3: {
            fontWeight: 600,
            fontSize: '1.5rem',
            lineHeight: 1.4,
          },
          h4: {
            fontWeight: 600,
            fontSize: '1.25rem',
            lineHeight: 1.4,
          },
          h5: {
            fontWeight: 500,
            fontSize: '1.125rem',
            lineHeight: 1.5,
          },
          h6: {
            fontWeight: 500,
            fontSize: '1rem',
            lineHeight: 1.5,
          },
          body1: {
            fontSize: '1rem',
            lineHeight: 1.6,
          },
          body2: {
            fontSize: '0.875rem',
            lineHeight: 1.6,
          },
          button: {
            textTransform: 'none',
            fontWeight: 600,
          },
          caption: {
            fontSize: '0.75rem',
            lineHeight: 1.5,
            fontWeight: 400,
          },
          overline: {
            fontSize: '0.875rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            lineHeight: 1.5,
          },
        },
        components: {
          MuiTypography: {
            variants: [
              {
                props: { variant: 'heroHeadline' },
                style: {
                  fontWeight: 700,
                  fontSize: '4.5rem',
                  lineHeight: 1.1,
                  letterSpacing: '-0.02em',
                  '@media (max-width:1200px)': {
                    fontSize: '3.5rem',
                  },
                  '@media (max-width:900px)': {
                    fontSize: '2.75rem',
                  },
                  '@media (max-width:600px)': {
                    fontSize: '2.25rem',
                  },
                },
              },
              {
                props: { variant: 'display1' },
                style: {
                  fontWeight: 700,
                  fontSize: '3.5rem',
                  lineHeight: 1.2,
                  letterSpacing: '0.01em',
                  '@media (max-width:900px)': {
                    fontSize: '2.5rem',
                  },
                  '@media (max-width:600px)': {
                    fontSize: '2rem',
                  },
                },
              },
              {
                props: { variant: 'statNumber' },
                style: {
                  fontWeight: 700,
                  fontSize: '3.5rem',
                  lineHeight: 1,
                  '@media (max-width:600px)': {
                    fontSize: '2.5rem',
                  },
                },
              },
              {
                props: { variant: 'cardTitle' },
                style: {
                  fontWeight: 700,
                  fontSize: '1.125rem',
                  lineHeight: 1.4,
                },
              },
            ],
          },
        },
      }),
    [mode]
  );

  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  return (
    <ThemeToggleContext.Provider value={{ toggleTheme, mode }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <Header />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/contact" element={<ContactPage />} />
          </Routes>
          <Footer />
        </Router>
      </ThemeProvider>
    </ThemeToggleContext.Provider>
  );
}